import { z } from 'zod';
import type { CanvasMountIdentity } from '../canvas/mountIdentity.js';
import { CanvasDocumentDepartSchema } from '../canvas/index.js';
import {
  ACT_SOURCE_REFERENCE_LIMIT,
  ActSourceKindSchema,
  ActSourceWebReferenceSchema,
  AvailableActCatalogEntrySchema,
} from '../act-catalog/index.js';

const CanonicalActIdSchema = z.string().regex(/^act_[0-9a-f]{32}$/);

export const RedactionProposedPayloadSchema = z.object({
  actId: CanonicalActIdSchema.optional(),
  nom_acte: z.string().nullable().optional(),
  specialisation: z.string().nullable().optional(),
  juridictions: z.array(z.string()).optional(),
  additional_instructions: z.string().optional(),
  skip_jurisprudence: z.boolean().optional(),
  /**
   * Lancement en un seul temps : la recherche et la rédaction s'enchaînent, il n'y a pas
   * de plan intermédiaire à valider. Le `kind` reste `'research'` — c'est lui qui ouvre le
   * formulaire de sources complet et qui déclenche la politique de lancement côté serveur.
   * Ce drapeau ne change que trois choses côté client : le flux SSE que le canvas écoute,
   * les libellés de la frise d'étapes, et le texte de la carte.
   */
  full_document: z.boolean().optional(),
  original_job_id: z.string().optional(),
  generation_mode: z.enum(['full', 'section_i', 'section_ii_iii']).optional(),
  availableActCatalog: z.array(AvailableActCatalogEntrySchema).optional(),
  /**
   * Présélection PROPOSÉE par l'agent, que la carte affiche déjà cochée.
   *
   * WHY une forme distincte du `sources` du retour : l'agent ne peut proposer
   * que des pièces DÉJÀ RANGÉES dans le dossier, jamais un fichier opaque. En
   * n'exposant ici que des `documentIds`, la référence `temporary_import` du
   * retour devient inatteignable depuis l'aller — un modèle ne peut pas forger
   * une source que personne n'a déposée.
   *
   * WHY ça reste une PROPOSITION : le serveur a validé le périmètre de chaque
   * id (dossier, cabinet, catégorie de la règle) avant de l'écrire ici, mais
   * c'est l'avocat qui coche. La carte n'affiche cochée qu'une pièce qu'elle
   * retrouve dans ses propres candidats, et le lancement repart de ce que
   * l'avocat a sous les yeux.
   */
  sources: z.array(z.object({
    kind: ActSourceKindSchema,
    documentIds: z.array(z.string().uuid()).min(1).max(ACT_SOURCE_REFERENCE_LIMIT),
  }).strict()).max(ActSourceKindSchema.options.length).optional(),
});

export const RedactionPlanRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  kind: z.literal('research'),
  jobId: z.string().min(1),
  /** Absolute server deadline; the browser only projects the remaining time. */
  autoLaunchAt: z.string().datetime({ offset: true }),
  proposedPayload: RedactionProposedPayloadSchema,
}).strict();

export const RedactionPlanRequestSchema = z.object({
  type: z.literal('redaction_plan_request'),
  timestamp: z.string().datetime(),
  payload: RedactionPlanRequestPayloadSchema,
}).strict();

/**
 * Rolling-deploy adapter for browsers that predate versioned draft snapshots.
 *
 * This frame is not an alternate UI contract: a capable connection always
 * uses `redaction_plan_draft_updated`. It remains accepted only so an already
 * open tab can safely disarm the old deadline while backend instances rotate.
 */
export const RedactionPlanInteractionSchema = z.object({
  type: z.literal('redaction_plan_interaction'),
  conversation_id: z.string().uuid().optional(),
  payload: z.object({ requestId: z.string().min(1) }).strict(),
}).strict();

/** ACK paired exclusively with the rolling-deploy interaction adapter. */
export const RedactionPlanInteractionAcknowledgedSchema = z.object({
  type: z.literal('redaction_plan_interaction_acknowledged'),
  timestamp: z.string().datetime({ offset: true }),
  payload: z.object({ requestId: z.string().min(1) }).strict(),
}).strict();

export const RedactionLaunchOriginSchema = z.enum([
  'manual',
  'automatic_deadline',
  'direct_after_user_instruction',
]);

/** Effective server-owned snapshot used to seed the progress card. */
export const RedactionLaunchEffectivePayloadSchema = RedactionProposedPayloadSchema.omit({
  availableActCatalog: true,
  sources: true,
}).strict();

const RedactionPlanSourcesSchema = z.array(z.object({
  kind: ActSourceKindSchema,
  references: z.array(ActSourceWebReferenceSchema).max(ACT_SOURCE_REFERENCE_LIMIT),
}).strict()).max(ActSourceKindSchema.options.length);

/** Authoritative acknowledgement shared by manual, automatic and direct launches. */
export const RedactionPlanLaunchStartedSchema = z.object({
  type: z.literal('redaction_plan_launch_started'),
  timestamp: z.string().datetime({ offset: true }),
  payload: z.object({
    requestId: z.string().min(1),
    jobId: z.string().min(1),
    kind: z.enum(['research', 'generation']),
    origin: RedactionLaunchOriginSchema,
    effectivePayload: RedactionLaunchEffectivePayloadSchema,
  }).strict(),
}).strict();

export const RedactionPlanResponsePayloadSchema = z.object({
  requestId: z.string().min(1),
  accepted: z.boolean(),
  additional_instructions: z.string().optional(),
  skip_jurisprudence: z.boolean().optional(),
  actId: CanonicalActIdSchema.optional(),
  // Kept only for mixed-version compatibility; canonical launches trust actId.
  nom_acte: z.string().optional(),
  specialisation: z.string().optional(),
  jurisdictions: z.array(z.string()).optional(),
  /**
   * The browser sends choices, never source policy. A Web upload is completed
   * through the authenticated multipart lifecycle first, then represented here
   * only by its opaque `temporary_import.importId`; Word-only `request_upload`
   * references cannot enter this frame.
   */
  sources: RedactionPlanSourcesSchema.optional(),
}).strict();

export const RedactionPlanResponseSchema = z.object({
  type: z.literal('redaction_plan_response'),
  conversation_id: z.string().uuid().optional(),
  payload: RedactionPlanResponsePayloadSchema,
});

/**
 * Full browser-visible draft sent when an edit rearms the one-shot deadline.
 *
 * Every displayed value is required so a valid frame cannot silently fall back
 * to the initial proposal. `actId: null` represents the UI state after a
 * specialty change cleared the previously selected act; the collector then
 * fails closed instead of launching a stale act at the deadline.
 */
export const RedactionPlanDraftSnapshotSchema = z.object({
  actId: CanonicalActIdSchema.nullable(),
  nom_acte: z.string(),
  specialisation: z.string(),
  jurisdictions: z.array(z.string()),
  additional_instructions: z.string(),
  skip_jurisprudence: z.boolean(),
  sources: RedactionPlanSourcesSchema,
}).strict();

/** Versioned edit that replaces the single server-owned plan deadline. */
export const RedactionPlanDraftUpdatedSchema = z.object({
  type: z.literal('redaction_plan_draft_updated'),
  conversation_id: z.string().uuid().optional(),
  payload: z.object({
    requestId: z.string().min(1),
    revision: z.number().int().positive(),
    snapshot: RedactionPlanDraftSnapshotSchema,
  }).strict(),
}).strict();

/** Backend acknowledgement carrying the authoritative deadline for a revision. */
export const RedactionPlanDraftUpdateAcknowledgedSchema = z.object({
  type: z.literal('redaction_plan_draft_update_acknowledged'),
  timestamp: z.string().datetime({ offset: true }),
  payload: z.object({
    requestId: z.string().min(1),
    revision: z.number().int().positive(),
    autoLaunchAt: z.string().datetime({ offset: true }),
  }).strict(),
}).strict();

/** Correlated Canvas admission handshake, completed before a web redaction is enqueued. */
export const CanvasAdmissionPreparePayloadSchema = z.object({
  requestId: z.string().min(1),
  jobId: z.string().min(1),
  conversationId: z.string().uuid(),
  kind: z.enum(['research', 'generation']),
  capturePlanMarkdown: z.boolean(),
  /**
   * Le papier à en-tête du travail. Présent sur une recherche (plan ou acte en un temps), il fait
   * démarrer un document NEUF sur ce modèle, quel que soit le document ouvert ; présent sur une
   * génération, c'est le modèle hérité du plan, sur lequel l'acte remplace le plan. Absent : un
   * job lancé avant ce champ, qui garde le comportement d'avant.
   */
  depart: CanvasDocumentDepartSchema.optional(),
}).strict();
export type CanvasAdmissionPreparePayload = z.infer<typeof CanvasAdmissionPreparePayloadSchema>;

/**
 * Combien de temps le backend attend le reçu `canvas_admission_prepared` (ou le
 * refus) après `canvas_admission_prepare`. Passé ce délai, le job de rédaction
 * est annulé avant la mise en file, et un reçu tardif n'est lu par personne.
 */
export const CANVAS_ADMISSION_ACK_TIMEOUT_MS = 30_000;

/**
 * L'échéance UNIQUE du gestionnaire navigateur de `canvas_admission_prepare` :
 * amorce du brouillon, montage du résident, capture, remplacement éventuel.
 *
 * Le montage à froid y reçoit tout ce qui reste, et pas la borne de 14 s des
 * autres montages : un premier chargement lent (module de l'éditeur,
 * construction SuperDoc) doit pouvoir aboutir tant que le backend attend.
 * Quatre secondes de marge pour le transit WebSocket, comme
 * `CANVAS_OPEN_DOCUMENT_BROWSER_DEADLINE_MS`.
 */
export const CANVAS_ADMISSION_BROWSER_DEADLINE_MS = CANVAS_ADMISSION_ACK_TIMEOUT_MS - 4_000;

/**
 * ⚠️ `conversationId` est ici la CORRÉLATION de la demande d'admission, pas
 * l'identité du montage : il est exigé des deux formes. C'est
 * `documentSessionId` seul qui distingue un client legacy d'un client
 * renouvelé, lequel s'identifie par `(conversationId, mountEpoch)` — les deux
 * champs étant alors déjà présents (#1335).
 */
const CanvasAdmissionPreparedLegacyPayloadSchema = z.object({
  requestId: z.string().min(1),
  jobId: z.string().min(1),
  conversationId: z.string().uuid(),
  documentSessionId: z.string().min(1),
  mountEpoch: z.number().int().nonnegative(),
  revision: z.string().min(1),
  planMarkdown: z.string().min(1).optional(),
}).strict();

/**
 * Le reçu d'admission tel que l'émettra un navigateur RENOUVELÉ (#1373).
 *
 * Exporté nommément et pas seulement enfoui dans l'union ci-dessous : c'est ce
 * schéma-là qu'un producteur visant la forme conversation doit appliquer.
 * Se valider contre l'union prouverait seulement qu'on a émis l'une des deux
 * formes, ce qu'un émetteur resté en legacy réussirait sans un bruit.
 *
 * ⚠️ `conversationId` reste présent des DEUX côtés — c'est la corrélation de la
 * demande, pas l'identité du montage. Ce qui distingue les deux formes est la
 * seule présence de `documentSessionId`, que ce schéma refuse : `.strict()`
 * vient du schéma legacy et survit à `omit`.
 */
export const CanvasAdmissionPreparedConversationPayloadSchema =
  CanvasAdmissionPreparedLegacyPayloadSchema.omit({ documentSessionId: true });
export type CanvasAdmissionPreparedConversationPayload =
  z.infer<typeof CanvasAdmissionPreparedConversationPayloadSchema>;

export const CanvasAdmissionPreparedPayloadSchema = z.union([
  CanvasAdmissionPreparedLegacyPayloadSchema,
  CanvasAdmissionPreparedConversationPayloadSchema,
]);
export type CanvasAdmissionPreparedPayload = z.infer<typeof CanvasAdmissionPreparedPayloadSchema>;

/**
 * L'identité du montage qu'un reçu d'admission désigne (#1335), ou que son
 * écho réénonce (#1374).
 *
 * ⚠️ **Ne pas passer ce reçu à `lireIdentiteDeMontage`.** Un reçu legacy porte
 * `documentSessionId` ET `conversationId` — le second étant la corrélation de
 * la demande, pas une identité de montage — et la lecture générique le
 * classerait « aliasé ». Ici c'est la seule présence de `documentSessionId` qui
 * tranche.
 *
 * Le reçu d'admission ET son écho `CanvasAdmissionEcho` passent par cette
 * MÊME porte (#1374) : les deux se discriminent sur la même clé, et deux
 * aiguillages séparés — un backend, un navigateur — sont exactement ce qui a
 * fait diverger les copies de l'écho la première fois.
 */
export function identiteDeMontageDuneAdmission(
  receipt: CanvasAdmissionPreparedPayload | CanvasAdmissionEcho,
): CanvasMountIdentity {
  return 'documentSessionId' in receipt
    ? { forme: 'legacy', documentSessionId: receipt.documentSessionId, mountEpoch: receipt.mountEpoch }
    : { forme: 'conversation', conversationId: receipt.conversationId, mountEpoch: receipt.mountEpoch };
}

/**
 * Le montage admis, RÉÉNONCÉ au navigateur sur `redaction_job_started` (#1335).
 *
 * Un type nommé et partagé, et non deux littéraux recopiés : l'émetteur
 * (`ResponseStreamer.sendRedactionJobStarted`) et le contrat de trame
 * (`types/hector-agent-edition-v3/websocket.ts`) portaient la même union mot
 * pour mot, et ils avaient DÉJÀ divergé sur un champ voisin (`full_document`,
 * présent chez l'un et absent chez l'autre). Un champ ajouté à l'un manquerait
 * silencieusement à l'autre.
 *
 * ⚠️ `mountEpoch` est requis dans les DEUX formes : le reçu d'admission
 * l'impose, donc le réénoncer sans lui perdrait ce que le backend sait déjà.
 */
export type CanvasAdmissionEcho =
  | { documentSessionId: string; mountEpoch: number; revision: string }
  | { conversationId: string; mountEpoch: number; revision: string };

export const CanvasAdmissionRefusedPayloadSchema = z.object({
  requestId: z.string().min(1),
  jobId: z.string().min(1),
  conversationId: z.string().uuid(),
  error: z.string().min(1),
}).strict();
export type CanvasAdmissionRefusedPayload = z.infer<typeof CanvasAdmissionRefusedPayloadSchema>;

export const CanvasAdmissionReleasePayloadSchema = z.object({
  requestId: z.string().min(1),
  jobId: z.string().min(1),
  conversationId: z.string().uuid(),
  reason: z.enum(['enqueue_failed', 'cancelled']),
}).strict();
export type CanvasAdmissionReleasePayload = z.infer<typeof CanvasAdmissionReleasePayloadSchema>;

export type RedactionProposedPayload = z.infer<typeof RedactionProposedPayloadSchema>;
export type RedactionPlanRequestPayload = z.infer<typeof RedactionPlanRequestPayloadSchema>;
export type RedactionPlanRequest = z.infer<typeof RedactionPlanRequestSchema>;
export type RedactionPlanInteraction = z.infer<typeof RedactionPlanInteractionSchema>;
export type RedactionPlanInteractionAcknowledged = z.infer<typeof RedactionPlanInteractionAcknowledgedSchema>;
export type RedactionPlanDraftUpdated = z.infer<typeof RedactionPlanDraftUpdatedSchema>;
export type RedactionPlanDraftSnapshot = z.infer<typeof RedactionPlanDraftSnapshotSchema>;
export type RedactionPlanDraftUpdateAcknowledged = z.infer<typeof RedactionPlanDraftUpdateAcknowledgedSchema>;
export type RedactionLaunchOrigin = z.infer<typeof RedactionLaunchOriginSchema>;
export type RedactionLaunchEffectivePayload = z.infer<typeof RedactionLaunchEffectivePayloadSchema>;
export type RedactionPlanLaunchStarted = z.infer<typeof RedactionPlanLaunchStartedSchema>;
export type RedactionPlanResponsePayload = z.infer<typeof RedactionPlanResponsePayloadSchema>;
export type RedactionPlanResponse = z.infer<typeof RedactionPlanResponseSchema>;
